import { useMemo, useState, useEffect } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import Pagination from "@/components/feature/Pagination";
import { newsList } from "@/mocks/news";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 6;

export default function NewsList() {
  const [keyword, setKeyword] = useState("");
  const [category, setCategory] = useState("全部");
  const [sortDesc, setSortDesc] = useState(true);
  const [page, setPage] = useState(1);

  useDocumentMeta(
    "动漫资讯 - 51动漫",
    "51动漫资讯频道汇集最新动漫行业动态、新番情报、作品评测与展会活动，按时间排序，助你快速了解动漫圈最新消息。"
  );

  const categories = useMemo(
    () => ["全部", ...Array.from(new Set(newsList.map((n) => n.category)))],
    []
  );

  useEffect(() => {
    setPage(1);
  }, [keyword, category, sortDesc]);

  const filtered = useMemo(() => {
    let list = [...newsList];
    if (category !== "全部") list = list.filter((n) => n.category === category);
    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      list = list.filter((n) => n.title.toLowerCase().includes(kw));
    }
    list.sort((a, b) => (sortDesc ? (a.date < b.date ? 1 : -1) : a.date < b.date ? -1 : 1));
    return list;
  }, [keyword, category, sortDesc]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetAll = () => {
    setKeyword("");
    setCategory("全部");
    setSortDesc(true);
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "资讯" }]} />

      <div className="mt-5 mb-6 md:mb-8">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          动漫资讯
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-500 max-w-3xl">
          关注动漫行业动态、新番情报与作品评测，按发布时间排序，第一时间了解动漫圈正在发生的事。
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索资讯标题"
              className="w-full h-11 pl-9 pr-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <button
            type="button"
            onClick={() => setSortDesc((v) => !v)}
            className="inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-md bg-background-100 border border-background-200 text-sm font-medium text-foreground-800 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className={sortDesc ? "ri-sort-desc" : "ri-sort-asc"}></i>
            {sortDesc ? "最新在前" : "最早在前"}
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                category === cat
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {paged.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {paged.map((news) => (
              <NewsCard key={news.id} news={news} layout="vertical" />
            ))}
          </div>
          <div className="mt-8 md:mt-10">
            <Pagination current={currentPage} total={totalPages} onChange={setPage} />
          </div>
        </>
      ) : (
        <div className="py-16 md:py-24 flex flex-col items-center justify-center text-center rounded-lg bg-background-100 border border-background-200">
          <span className="w-16 h-16 rounded-full bg-background-200 text-foreground-400 flex items-center justify-center mb-4">
            <i className="ri-search-eye-line text-3xl"></i>
          </span>
          <h3 className="font-heading font-bold text-lg text-foreground-950 mb-2">
            没有找到相关资讯
          </h3>
          <p className="text-sm text-foreground-500 mb-5 max-w-sm">
            换个关键词或分类试试，也许就能找到你感兴趣的内容。
          </p>
          <button
            type="button"
            onClick={resetAll}
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            重置搜索条件
          </button>
        </div>
      )}
    </div>
  );
}